import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/siteData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center text-foreground-300">
            <i className="ri-file-search-line text-5xl"></i>
          </div>
          <h2 className="text-xl font-bold font-heading text-foreground-900 mb-2">
            文章未找到
          </h2>
          <p className="text-sm text-foreground-500 mb-6">
            您访问的文章不存在或已被移除
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-foreground-900 text-background-50 rounded-full text-sm font-medium hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => article.relatedSlugs.includes(a.slug))
    .slice(0, 3);

  return (
    <div className="pt-16 bg-background-50">
      <Breadcrumb
        items={[
          { label: '首页', href: '/' },
          { label: article.categoryName, href: `/category/${article.category}` },
          { label: article.title },
        ]}
      />

      {/* Article Header */}
      <section className="w-full py-8 md:py-12">
        <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <Link
              to={`/category/${article.category}`}
              className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full hover:bg-primary-200 transition-colors whitespace-nowrap"
            >
              {article.categoryName}
            </Link>
          </div>
          <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-900 mb-4 leading-tight">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-400 mb-6">
            <span className="flex items-center gap-1">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-calendar-line"></i>
              </span>
              {article.date}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-time-line"></i>
              </span>
              {article.readTime}
            </span>
          </div>
          <div className="w-full aspect-[16/9] rounded-xl overflow-hidden mb-8">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="w-full pb-10 md:pb-14">
        <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
          <article className="prose-custom">
            {article.content.map((block, idx) => {
              if (block.type === 'h2') {
                return (
                  <h2 key={idx} className="text-xl font-bold font-heading text-foreground-900 mt-8 mb-4">
                    {block.text}
                  </h2>
                );
              }
              return (
                <p key={idx} className="text-base text-foreground-700 leading-relaxed mb-4">
                  {block.text}
                </p>
              );
            })}
          </article>

          {/* Tags */}
          <div className="flex flex-wrap items-center gap-2 mt-10 pt-6 border-t border-background-200/70">
            <span className="text-sm text-foreground-500">标签：</span>
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full border border-background-200/70"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="w-full py-12 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-6 md:mb-8">
              相关阅读
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
              {relatedArticles.map((rel) => (
                <article
                  key={rel.slug}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 transition-colors hover:border-background-300/70"
                >
                  <Link to={`/article/${rel.slug}`} className="block">
                    <div className="w-full aspect-[16/10] overflow-hidden">
                      <img
                        src={rel.image}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                  </Link>
                  <div className="p-5">
                    <div className="flex items-center gap-3 text-xs text-foreground-400 mb-3">
                      <span>{rel.categoryName}</span>
                      <span>·</span>
                      <span>{rel.date}</span>
                    </div>
                    <h3 className="text-base font-bold font-heading text-foreground-900 mb-2 leading-snug group-hover:text-primary-600 transition-colors">
                      <Link to={`/article/${rel.slug}`}>
                        {rel.title}
                      </Link>
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                      {rel.excerpt}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Schema.org Article */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Article',
        'headline': article.title,
        'description': article.excerpt,
        'datePublished': article.date,
        'author': {
          '@type': 'Organization',
          'name': '福利姬完全指南',
        },
        'publisher': {
          '@type': 'Organization',
          'name': '福利姬完全指南',
        },
        'image': article.image,
        'articleSection': article.categoryName,
        'keywords': article.tags.join(', '),
        'inLanguage': 'zh-CN',
      }) }}></script>
    </div>
  );
}